// libraries.
import { Container } from 'react-bootstrap';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

// components
import { VersionTag, LoginHeader, AutoLoginLoader } from './components';

// styles.
import { ImageHero } from '@assets/images';
import { WrapperLogin } from './styles';

// Auto-login utilities
import { 
  getAutoLoginData, 
  clearAutoLoginParams, 
  saveAutoLoginData 
} from '@domains/user/utils/auto-login';
import { ButtonGoogle, setUser, setRolesFromCognito, setCountry, setCountriesOptions, useLazyGetUserRoleQuery } from '@domains/user';

export const LoginPageSSO = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [getUserRole] = useLazyGetUserRoleQuery();
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);

  const authKey = process.env.ENV_GOOGLE_AUTH;

  const loginOnSuccess = (roles?: string[]) => {
    const roleRoutes: Record<string, string> = {
      'comercial': '/orchestrator-pricing-committee/list-opc',
      'idm-legal': '/orchestrator-pricing-committee/max-price-opc',
    };

    const route = roles?.find(role => roleRoutes[role]);
    navigate(route ? roleRoutes[route] : '/home');
  };

  // 🔐 AUTO-LOGIN: Check for SSO parameters from Console
  useEffect(() => {
    const autoLoginData = getAutoLoginData();
    
    if (autoLoginData) {
      console.log('🔐 Auto-login detected from Console (minimal data)');
      setIsAutoLoggingIn(true); // Show loading state
      
      // 1. Save minimal data to localStorage (token + googleId)
      saveAutoLoginData(autoLoginData);
      
      // 2. Fetch Google profile using the access token
      console.log('📡 Fetching Google profile...');
      fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: {
          Authorization: `Bearer ${autoLoginData.token}`,
        },
      })
        .then((res) => res.json())
        .then((profile) => {
          console.log('✅ Google profile fetched');
          
          // 3. Set user data from Google profile
          dispatch(setUser({
            email: profile.email,
            familyName: profile.family_name,
            givenName: profile.given_name,
            googleId: profile.sub,
            imageUrl: profile.picture,
            name: profile.name,
          }));
          
          // 4. Fetch full user data from Cognito using googleId
          console.log('📡 Fetching user roles from Cognito...');
          return getUserRole({
            userId: profile.sub,
            country: 'MX', // Default country, API returns all user's countries
            external: false,
          }).unwrap();
        })
        .then((cognitoData) => {
          console.log('✅ User roles fetched from Cognito');
          
          // 5. Set roles from Cognito
          dispatch(setRolesFromCognito(cognitoData.roles));
          
          // 6. Set countries from Cognito
          dispatch(setCountriesOptions(cognitoData.countriesSelect));
          
          // 7. Set active country (first one in the list)
          const activeCountry = cognitoData.countriesByUser[0];
          if (activeCountry) {
            dispatch(setCountry(activeCountry));
          }
          
          // 8. Clear URL params (security)
          clearAutoLoginParams();
          
          // 9. Navigate to appropriate route based on roles
          console.log('✅ Auto-login successful. Redirecting...');
          loginOnSuccess(cognitoData.roles);
        })
        .catch((error) => {
          console.error('❌ Auto-login failed:', error);
          // Clear params and stay on login page
          clearAutoLoginParams();
          setIsAutoLoggingIn(false); // Hide loading, show login form
        });
    }
  }, [dispatch, navigate, getUserRole]);

  return (
    <Container fluid className="p-0">
      <div className="d-flex">
        <div className="p-0">
          <ImageHero style={{ height: '100vh' }} />
        </div>
        <div className="d-flex align-items-center justify-content-center w-100 mb-5">
          {isAutoLoggingIn ? (
            <AutoLoginLoader
              title="Iniciando sesión automáticamente..."
              subtitle="Verificando credenciales desde HWS Console"
            />
          ) : (
            <WrapperLogin className="mb-5">
              <div className="d-flex flex-column align-items-center gap-3 mb-5">
                <LoginHeader
                  subtitle={
                    <div className="d-flex gap-2">
                      <div>Polynator</div>/<div>Trámites</div>
                    </div>
                  }
                />
                <div className="google">
                  Inicia sesión con tu <br /> cuenta de Google
                </div>
                {authKey ? (
                  <GoogleOAuthProvider clientId={authKey}>
                    <ButtonGoogle onSuccess={loginOnSuccess} />
                  </GoogleOAuthProvider>
                ) : (
                  <div className="text-danger">
                    No se ha encontrado la clave de autenticación
                  </div>
                )}
              </div>
            </WrapperLogin>
          )}
        </div>
      </div>
      <VersionTag />
    </Container>
  );
};


